import { useToast } from 'vue-toastification';

// Get toast instance
let toast = null;

export default {
  namespaced: true,
  
  state: {
    notifications: [],
    unreadCount: 0,
    showNotificationCenter: false,
  },
  
  mutations: {
    ADD_NOTIFICATION(state, notification) {
      const newNotification = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        read: false,
        ...notification,
      };
      
      state.notifications.unshift(newNotification);
      state.unreadCount++;
      
      // Keep only last 100 notifications
      if (state.notifications.length > 100) {
        state.notifications = state.notifications.slice(0, 100);
      }
    },
    
    MARK_AS_READ(state, notificationId) {
      const notification = state.notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    
    MARK_ALL_AS_READ(state) {
      state.notifications.forEach(n => n.read = true);
      state.unreadCount = 0;
    },
    
    REMOVE_NOTIFICATION(state, notificationId) {
      const index = state.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        if (!state.notifications[index].read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
    
    CLEAR_ALL_NOTIFICATIONS(state) {
      state.notifications = [];
      state.unreadCount = 0;
    },
    
    TOGGLE_NOTIFICATION_CENTER(state) {
      state.showNotificationCenter = !state.showNotificationCenter;
    },
    
    SET_NOTIFICATION_CENTER(state, show) {
      state.showNotificationCenter = show;
    },
  },
  
  actions: {
    // Initialize toast instance
    initToast({ commit }) {
      toast = useToast();
    },
    
    // Show different types of notifications
    showSuccess({ commit }, message, options = {}) {
      if (!toast) toast = useToast();
      
      toast.success(message, {
        timeout: 3000,
        ...options,
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'success',
        title: 'Sucesso',
        message,
        icon: 'fas fa-check-circle',
      });
    },
    
    showError({ commit }, message, options = {}) {
      if (!toast) toast = useToast();
      
      toast.error(message, {
        timeout: 5000,
        ...options,
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'error',
        title: 'Erro',
        message,
        icon: 'fas fa-exclamation-circle',
      });
    },
    
    showWarning({ commit }, message, options = {}) {
      if (!toast) toast = useToast();
      
      toast.warning(message, {
        timeout: 4000,
        ...options,
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'warning',
        title: 'Atenção',
        message,
        icon: 'fas fa-exclamation-triangle',
      });
    },
    
    showInfo({ commit }, message, options = {}) {
      if (!toast) toast = useToast();
      
      toast.info(message, {
        timeout: 3000,
        ...options,
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'info',
        title: 'Informação',
        message,
        icon: 'fas fa-info-circle',
      });
    },
    
    // Game-specific notifications
    showBetPlaced({ commit }, { amount, game }) {
      if (!toast) toast = useToast();
      
      const message = `Aposta de R$ ${amount.toFixed(2)} realizada em ${game}`;
      
      toast.success(message, {
        timeout: 3000,
        icon: '💰',
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'bet',
        title: 'Aposta Realizada',
        message,
        icon: 'fas fa-coins',
        data: { amount, game },
      });
    },
    
    showBetWon({ commit }, { amount, payout, game }) {
      if (!toast) toast = useToast();
      
      const profit = payout - amount;
      const message = `Você ganhou R$ ${profit.toFixed(2)} em ${game}!`;
      
      toast.success(message, {
        timeout: 5000,
        icon: '🎉',
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'win',
        title: 'Aposta Vencedora!',
        message,
        icon: 'fas fa-trophy',
        data: { amount, payout, profit, game },
      });
    },
    
    showBetLost({ commit }, { amount, game }) {
      if (!toast) toast = useToast();
      
      const message = `Você perdeu R$ ${amount.toFixed(2)} em ${game}`;
      
      toast.error(message, {
        timeout: 3000,
        icon: '😔',
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'loss',
        title: 'Aposta Perdida',
        message,
        icon: 'fas fa-times-circle',
        data: { amount, game },
      });
    },
    
    showWithdrawalApproved({ commit }, { amount }) {
      if (!toast) toast = useToast();
      
      const message = `Seu saque de R$ ${amount.toFixed(2)} foi aprovado!`;
      
      toast.success(message, {
        timeout: 5000,
        icon: '💸',
      });
      
      commit('ADD_NOTIFICATION', {
        type: 'withdrawal',
        title: 'Saque Aprovado',
        message,
        icon: 'fas fa-check-circle',
        data: { amount },
      });
    },
    
    // Notification management
    markAsRead({ commit }, notificationId) {
      commit('MARK_AS_READ', notificationId);
    },
    
    markAllAsRead({ commit }) {
      commit('MARK_ALL_AS_READ');
    },
    
    removeNotification({ commit }, notificationId) {
      commit('REMOVE_NOTIFICATION', notificationId);
    },
    
    clearAllNotifications({ commit }) {
      commit('CLEAR_ALL_NOTIFICATIONS');
    },
    
    toggleNotificationCenter({ commit }) {
      commit('TOGGLE_NOTIFICATION_CENTER');
    },
    
    showNotificationCenter({ commit }) {
      commit('SET_NOTIFICATION_CENTER', true);
    },
    
    hideNotificationCenter({ commit }) {
      commit('SET_NOTIFICATION_CENTER', false);
    },
  },
  
  getters: {
    allNotifications: state => state.notifications,
    
    unreadNotifications: state => {
      return state.notifications.filter(n => !n.read);
    },
    
    unreadCount: state => state.unreadCount,
    
    notificationsByType: state => type => {
      return state.notifications.filter(n => n.type === type);
    },
    
    recentNotifications: state => {
      return state.notifications.slice(0, 10);
    },
    
    hasUnreadNotifications: state => state.unreadCount > 0,
    
    showNotificationCenter: state => state.showNotificationCenter,
    
    // Group notifications by date
    notificationsByDate: state => {
      const groups = {};
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      
      state.notifications.forEach(notification => {
        const date = new Date(notification.timestamp).toDateString();
        let groupKey;
        
        if (date === today) {
          groupKey = 'Hoje';
        } else if (date === yesterday) {
          groupKey = 'Ontem';
        } else {
          groupKey = new Date(notification.timestamp).toLocaleDateString('pt-BR');
        }
        
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(notification);
      });
      
      return groups;
    },
  },
};